import { useEffect, useMemo, useState } from 'react';
import { getSafeSupabaseClient } from '@/lib/supabaseSync';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import type { OrgPathNode } from '../types';

interface RosterUser {
  id: string;
  name: string;
  email: string;
}

interface RosterGroup {
  id: string;
  name: string;
}

/**
 * Per-node assignee picker data: the workspace roster/group directory plus
 * which of them are actually valid (assigned) at each org node in this
 * cycle's path design. "Valid at that node" per the owner's ruling — the
 * picker must not offer just anyone in the workspace.
 */
export function useOrgNodeRoster(orgNodes: OrgPathNode[]) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [roster, setRoster] = useState<RosterUser[]>([]);
  const [groups, setGroups] = useState<RosterGroup[]>([]);
  const [usersByNode, setUsersByNode] = useState<Map<string, string[]>>(new Map());
  const [groupsByNode, setGroupsByNode] = useState<Map<string, string[]>>(new Map());
  const [loading, setLoading] = useState(false);

  const nodeIds = useMemo(() => orgNodes.map((n) => n.id), [orgNodes]);

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !workspaceId || nodeIds.length === 0) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [{ data: rpcData }, { data: groupData }, { data: userAssignments }, { data: groupAssignments }] =
          await Promise.all([
            supabase.rpc('get_workspace_participants', { p_workspace_id: workspaceId }),
            supabase
              .from('groups')
              .select('id, name, display_name')
              .or(`workspace_id.eq.${workspaceId},workspace_id.is.null`)
              .order('name'),
            supabase
              .from('user_node_assignments')
              .select('user_id, org_unit_id')
              .in('org_unit_id', nodeIds)
              .eq('active', true),
            supabase
              .from('group_node_assignments')
              .select('group_id, org_unit_id')
              .in('org_unit_id', nodeIds),
          ]);
        if (cancelled) return;

        setRoster(
          (rpcData ?? []).map((u: Record<string, unknown>) => ({
            id: u.id as string,
            name: ((u.name as string) || (u.email as string)) ?? '',
            email: (u.email as string) ?? '',
          })),
        );
        setGroups(
          (groupData ?? []).map((g: { id: string; name?: string; display_name?: string }) => ({
            id: g.id,
            name: g.display_name || g.name || g.id,
          })),
        );

        const byUserNode = new Map<string, string[]>();
        (userAssignments ?? []).forEach((row: { user_id: string; org_unit_id: string }) => {
          const arr = byUserNode.get(row.org_unit_id) ?? [];
          arr.push(row.user_id);
          byUserNode.set(row.org_unit_id, arr);
        });
        setUsersByNode(byUserNode);

        const byGroupNode = new Map<string, string[]>();
        (groupAssignments ?? []).forEach((row: { group_id: string; org_unit_id: string }) => {
          const arr = byGroupNode.get(row.org_unit_id) ?? [];
          arr.push(row.group_id);
          byGroupNode.set(row.org_unit_id, arr);
        });
        setGroupsByNode(byGroupNode);
      } catch (e) {
        console.warn('[useOrgNodeRoster] Failed to load roster:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // nodeIds is derived every render from orgNodes; join guards against
    // re-fetching when the array reference changes but contents don't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, nodeIds.join(',')]);

  const rosterById = useMemo(() => new Map(roster.map((u) => [u.id, u])), [roster]);
  const groupsById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const usersAtNode = (nodeId: string): RosterUser[] =>
    (usersByNode.get(nodeId) ?? [])
      .map((id) => rosterById.get(id))
      .filter((u): u is RosterUser => Boolean(u));

  const groupsAtNode = (nodeId: string): RosterGroup[] =>
    (groupsByNode.get(nodeId) ?? [])
      .map((id) => groupsById.get(id))
      .filter((g): g is RosterGroup => Boolean(g));

  return { loading, usersAtNode, groupsAtNode };
}
